import React, { useState } from 'react';
import { FileDown, Package, ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { specificationEvent } from '../../lib/specification-events';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  sample: {
    title: 'Request a sample',
    blurb: 'Request a sample of the selected article. Availability, configuration and supply terms require confirmation.',
    cta: 'Send sample request',
  },
  boq: {
    title: 'Send your BOQ for pricing',
    blurb: 'Provide article quantities and project requirements for a pricing and lead-time review.',
    cta: 'Send BOQ',
  },
};

/**
 * Specifier conversion actions. Every submission carries the exact article
 * codes (and quantities for a BOQ) through to the sales inbox.
 */
export function SpecifierActions({ articles = [], family, collection, className = '' }) {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [packing, setPacking] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', project_name: '', city: '', required_by: '', message: '' });
  const [qty, setQty] = useState({});

  const codes = articles.map(a => a.base_code || a.art_no).filter(Boolean);

  const downloadSpecPack = async () => {
    if (!codes.length) return;
    setPacking(true);
    try {
      const res = await fetch(`${BACKEND}/api/specifier/spec-pack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base_codes: codes }),
      });
      if (!res.ok) throw new Error('Spec pack could not be generated');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LUMIBRIGHT-${(family || 'spec-pack').toLowerCase()}-spec-pack.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Spec pack ready — ${codes.length} datasheet${codes.length === 1 ? '' : 's'} in one PDF`);
      specificationEvent('download_technical_file', { file_type: 'spec_pack', page_type: 'family' });
    } catch (err) {
      toast.error(err.message || 'Spec pack failed');
    } finally {
      setPacking(false);
    }
  };

  const [errors, setErrors] = useState({});

  const submit = async () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name';
    if (!form.email.trim()) next.email = 'Please enter your work email';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) next.email = 'Enter a valid email address';
    setErrors(next);
    if (Object.keys(next).length) {
      toast.error(Object.values(next)[0]);
      return;
    }
    setBusy(true);
    try {
      const items = articles.map(a => ({
        base_code: a.base_code || a.art_no,
        family,
        collection,
        order_code: a.order_code || null,
        quantity: Number(qty[a.base_code || a.art_no] || (mode === 'sample' ? 1 : 0)) || (mode === 'sample' ? 1 : 0),
      })).filter(i => i.base_code && (mode === 'sample' ? true : i.quantity > 0));

      if (!items.length) {
        toast.error(mode === 'boq' ? 'Enter a quantity against at least one article' : 'No article selected');
        setBusy(false);
        return;
      }

      const res = await fetch(`${BACKEND}/api/specifier/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: mode, ...form, items, source_url: window.location.pathname,
          landing_page: sessionStorage.getItem('lb_landing') || window.location.pathname,
          referrer: document.referrer || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not send your request');
      toast.success(`Received — your reference is ${data.reference}`, {
        description: 'Your request is recorded in our enquiry inbox.',
      });
      specificationEvent(mode === 'sample' ? 'request_sample' : 'submit_project_enquiry', { page_type: 'family' }, data.enquiry_id);
      setMode(null);
      setForm({ name: '', email: '', company: '', phone: '', project_name: '', city: '', required_by: '', message: '' });
      setQty({});
    } catch (err) {
      toast.error(err.message || 'Could not send your request');
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = 'text', required = false) => (
    <div className="space-y-1.5">
      <Label htmlFor={`spec-${key}`} className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}{required ? ' *' : ''}
      </Label>
      <Input id={`spec-${key}`} type={type} value={form[key]}
        aria-invalid={!!errors[key]}
        className={errors[key] ? 'border-red-500' : undefined}
        onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); setErrors(er => ({ ...er, [key]: undefined })); }}
        data-testid={`specifier-field-${key}`} />
      {errors[key] && (
        <p role="alert" className="text-xs text-red-600" data-testid={`specifier-error-${key}`}>{errors[key]}</p>
      )}
    </div>
  );

  return (
    <div className={className} data-testid="specifier-actions">
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setMode('sample')} data-testid="specifier-request-sample-btn" className="gap-2">
          <Package className="w-4 h-4" /> Request sample
        </Button>
        <Button variant="outline" onClick={downloadSpecPack} disabled={packing || !codes.length}
          data-testid="specifier-spec-pack-btn" className="gap-2">
          {packing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          Download spec pack{codes.length > 1 ? ` (${codes.length})` : ''}
        </Button>
        <Button variant="outline" onClick={() => setMode('boq')} data-testid="specifier-boq-btn" className="gap-2">
          <ClipboardList className="w-4 h-4" /> Send BOQ
        </Button>
      </div>

      <Dialog open={!!mode} onOpenChange={open => !open && setMode(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="specifier-dialog">
          <DialogHeader>
            <DialogTitle>{mode ? COPY[mode].title : ''}</DialogTitle>
            <DialogDescription>{mode ? COPY[mode].blurb : ''}</DialogDescription>
          </DialogHeader>

          <div className="grid sm:grid-cols-2 gap-4">
            {field('name', 'Name', 'text', true)}
            {field('email', 'Work email', 'email', true)}
            {field('company', 'Company')}
            {field('phone', 'Phone')}
            {field('project_name', 'Project')}
            {field('city', 'City')}
            {mode === 'boq' && field('required_by', 'Required by')}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Articles {mode === 'boq' ? '— enter quantities' : 'included'}
            </Label>
            <div className="border border-border rounded-md divide-y divide-border max-h-56 overflow-y-auto"
              data-testid="specifier-article-list">
              {articles.map(a => {
                const code = a.base_code || a.art_no;
                return (
                  <div key={code} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="font-mono text-xs w-28 shrink-0">{code}</span>
                    <span className="flex-1 truncate text-muted-foreground">
                      {[a.power, a.color_temp, a.beam_angle, a.dimension].filter(Boolean).join(' · ') || family}
                    </span>
                    {mode === 'boq' && (
                      <Input type="number" min="0" className="w-20 h-8" placeholder="0"
                        value={qty[code] || ''}
                        onChange={e => setQty(q => ({ ...q, [code]: e.target.value }))}
                        data-testid={`specifier-qty-${code}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="spec-message" className="text-xs uppercase tracking-wider text-muted-foreground">
              Notes
            </Label>
            <Textarea id="spec-message" rows={3} value={form.message}
              placeholder={mode === 'boq' ? 'Control protocol, finish, delivery address…' : 'Which finish or beam should the sample be?'}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              data-testid="specifier-field-message" />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setMode(null)} data-testid="specifier-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="specifier-submit-btn" className="gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode ? COPY[mode].cta : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SpecifierActions;
